<template>
  <div
    class="plan-summary d-flex justify-content-between align-items-center flex-wrap gap-3"
  >
    <div>
      <div v-if="openMwh > 0" class="fw-semibold text-warning-emphasis">
        {{
          $t("forwards.deals.plan.summary_open", {
            mwh: formatNumberWithSpaces(openMwh),
          })
        }}
      </div>
      <div v-else class="fw-semibold">
        {{ $t("forwards.deals.plan.summary_fully_committed") }}
      </div>
      <div class="text-body-tertiary fs-10">
        {{
          $t("forwards.deals.plan.summary_totals", {
            planned: formatNumberWithSpaces(plannedMwh),
            committed: formatNumberWithSpaces(committedMwh),
            escrowed: formatNumberWithSpaces(escrowedMwh),
          })
        }}
        ·
        <span v-if="dealVolumeMwh !== null">{{
          $t("forwards.deals.plan.summary_deal_volume", {
            mwh: formatNumberWithSpaces(dealVolumeMwh),
          })
        }}</span>
        <span v-else>{{
          $t("forwards.deals.plan.summary_no_deal_volume")
        }}</span>
      </div>
    </div>

    <div class="d-flex align-items-center gap-2 flex-wrap">
      <span
        v-if="capabilities.confirm_blocked_reason && showConfirm"
        class="text-body-tertiary fs-10 confirm-reason"
        >{{ capabilities.confirm_blocked_reason }}</span
      >
      <ActionButtonWithSpinner
        v-if="showConfirm"
        :busy="busyAction === 'confirm'"
        :disabled="!capabilities.can_confirm || busyAction !== null"
        variant="btn-primary"
        @click="emit('confirm')"
      >
        {{ $t("forwards.deals.plan.confirm") }}
      </ActionButtonWithSpinner>
      <ActionButtonWithSpinner
        v-if="capabilities.can_unconfirm"
        :busy="busyAction === 'unconfirm'"
        :disabled="busyAction !== null"
        variant="btn-outline-secondary"
        @click="emit('unconfirm')"
      >
        {{ $t("forwards.deals.plan.unconfirm") }}
      </ActionButtonWithSpinner>
      <ActionButtonWithSpinner
        v-if="capabilities.can_cancel"
        :busy="busyAction === 'cancel'"
        :disabled="busyAction !== null"
        variant="btn-outline-danger"
        @click="emit('cancel')"
      >
        {{ $t("forwards.deals.plan.cancel_deal") }}
      </ActionButtonWithSpinner>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { DealCapabilities, DealStatus } from "@/forwards2/forwardsTypes";
import type { DealAction } from "@/forwards2/deals/useDealStore";
import { computed } from "vue";

/**
 * The plan's bottom line, and the lifecycle actions that act on it.
 *
 * Confirm stays visible while the deal is a draft even when it cannot be pressed: its blocking
 * reason — usually the open volume beside it — is the one thing the user has to act on next. That
 * holds for whoever may manage the deal; a viewer who may not is offered nothing to act on.
 * Cancel follows `capabilities` rather than a local rule, because who may cancel changes once the
 * deal is confirmed (owner while draft, admin after). Back to draft is offered only when it would
 * succeed; a deal that has escrowed anything never qualifies, so a disabled button would sit on
 * nearly every confirmed deal.
 */
const props = withDefaults(
  defineProps<{
    status: DealStatus;
    capabilities: DealCapabilities;
    openMwh: number;
    plannedMwh: number;
    committedMwh: number;
    escrowedMwh: number;
    /** The stored deal volume, not the sum of commitments (plan §1.1 "Deal volume column"). */
    dealVolumeMwh: number | null;
    busyAction?: DealAction | null;
  }>(),
  { busyAction: null }
);

const emit = defineEmits<{
  confirm: [];
  unconfirm: [];
  cancel: [];
}>();

const showConfirm = computed(
  () => props.status === "draft" && props.capabilities.can_manage
);
</script>

<style scoped lang="scss">
.plan-summary {
  border-top: 1px solid var(--phoenix-gray-200);
  padding-top: 0.75rem;
  margin-top: 0.75rem;
}

.confirm-reason {
  max-width: 22rem;
}
</style>
